import { FC } from "react";

import { DonationFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveEditorStepFundraisingGoalStateless } from "./LiveEditorStepFundraisingGoal.stateless";
import { StepFundraisingGoalFormData } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";

export const DonationLiveEditorStepFundraisingGoal: FC = () => {
  const { t } = useTranslate();
  const {
    apiFormObject: apiDonationForm,
    formCategory,
    optionalFundraiserToken,
  } = useLiveFormEditorContext<"donation-form">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const hadInitialTarget = apiDonationForm.target !== null;
  const defaultValues: StepFundraisingGoalFormData = {
    target: apiDonationForm.target || null,
    showInput: apiDonationForm.target !== null,
  };

  const form = useForm<StepFundraisingGoalFormData>({ defaultValues });

  const target = form.watch("target");
  const showTargetInput = form.watch("showInput");

  const { mutateAsync: updateTarget } = trpc.updateDonationFormTarget.useMutation();
  const saveData = async (formData: StepFundraisingGoalFormData) => {
    return updateTarget({
      donationFormId: apiDonationForm.id,
      target: formData.target,
      fundraiserToken: optionalFundraiserToken,
    });
  };

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepFundraisingGoalFormData>({
    type: "donation-form",
    form,
    saveData,
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const targetInPreview = formData.target || 100000;
      liveDataToMutate.donation.target = targetInPreview;
      liveDataToMutate.placeholders.isFundraisingGoal = !formData.target;
    },
  });

  return (
    <LiveEditorStepFundraisingGoalStateless
      title={t("dashboard", "liveFormEditor.stepFundraisingGoal.donationForm.title")}
      description={t("dashboard", "liveFormEditor.stepFundraisingGoal.donationForm.description")}
      addButtonLabel={t("dashboard", "donationForm.addFundraisingGoal")}
      form={form}
      handleSaveAndBack={handleSaveAndBack}
      handleSaveAndNext={handleSaveAndNext}
      target={target}
      showTargetInput={showTargetInput}
      onAddFundraisingGoal={() => logUserActionEvent(AmplitudeEvents.FundraisingGoalAdded)}
      onRemoveFundraisingGoal={() => logUserActionEvent(AmplitudeEvents.FundraisingGoalRemoved)}
      hadInitialTarget={hadInitialTarget}
      isGoalMandatory={formCategory === DonationFormCategory.PeerToPeerCampaign}
    />
  );
};
